import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { BancoldexService } from '../../../comun/services/bancoldex.service';
import { ParametrosService } from '../../../services/parametros.service';
import { UserService } from '../../../services/user.service';
import { PerfilSeccionId } from '../../data/perfil-microempresario.content';
import {
  PerfilTipoDocumentosApi,
  isPerfilJuridica,
  resolveTipoDocumentosPerfilApi,
} from '../perfil-tipo.util';
import { PerfilDocumentoModalConfig } from '../shared/perfil-documento-modal/perfil-documento-modal.model';
import { PerfilDocumentoDef } from '../progress/perfil-progress.definitions';
import { PerfilProgressService } from '../progress/perfil-progress.service';
import { PerfilSegmentoService } from '../segmento/perfil-segmento.service';
import {
  PAR_TAMANO_MAX_ARCHIVOS,
  PAR_TAMANO_MAX_MULTIMEDIA,
  buildDocumentoModalConfig,
  isMultimediaDoc,
  mapGruposApiToPerfilDocumentos,
  parseGruposPerfilResponse,
} from './perfil-documentos.util';

interface ParametroFilterRow {
  valorParametro?: string;
}

@Injectable({ providedIn: 'root' })
export class PerfilDocumentosService {
  private readonly docsSubject = new BehaviorSubject<PerfilDocumentoDef[]>([]);
  private loadedForTipo: string | null = null;
  private readonly urlCache = new Map<number, Observable<string | null>>();
  /** Petición de catálogo en vuelo (evita doble HTTP si varios subscribe a la vez). */
  private loadInFlight$: Observable<PerfilDocumentoDef[]> | null = null;
  private loadInFlightTipo: string | null = null;
  private tamanoDocsMb: number | null = null;
  private tamanoMultimediaMb: number | null = null;
  private tamanosLoaded = false;
  private tamanosLoad$: Observable<void> | null = null;

  readonly documentos$: Observable<PerfilDocumentoDef[]> =
    this.docsSubject.asObservable();

  constructor(
    private readonly parametros: ParametrosService,
    private readonly progress: PerfilProgressService,
    private readonly segmentoService: PerfilSegmentoService,
    private readonly bancoldex: BancoldexService,
    private readonly userService: UserService
  ) {}

  get documentos(): ReadonlyArray<PerfilDocumentoDef> {
    return this.docsSubject.value;
  }

  /** Docs de una sección hub (`sobre-ti` | `sobre-negocio` | `sobre-ingresos`). */
  forSeccion(seccionId: PerfilSeccionId | string): PerfilDocumentoDef[] {
    return this.docsSubject.value.filter((d) => d.seccionId === seccionId);
  }

  findByValueKey(valueKey: string): PerfilDocumentoDef | undefined {
    return this.docsSubject.value.find((d) => d.valueKey === valueKey);
  }

  /** MB según PAR-122 (docs) o PAR-056 (video/multimedia). */
  tamanoMaxMbFor(docOrValueKey: PerfilDocumentoDef | string): number | null {
    const doc =
      typeof docOrValueKey === 'string'
        ? this.findByValueKey(docOrValueKey)
        : docOrValueKey;
    const multimedia = doc ? isMultimediaDoc(doc) : false;
    return multimedia ? this.tamanoMultimediaMb : this.tamanoDocsMb;
  }

  
  ensureLoaded(): Observable<PerfilDocumentoDef[]> {
    const tipo = resolveTipoDocumentosPerfilApi(
      this.userService.getUser()?.tipoPersona,
      this.segmentoService.segmento
    );
    if (this.loadedForTipo === tipo && this.docsSubject.value.length) {
      return this.ensureTamanosLoaded().pipe(map(() => this.docsSubject.value));
    }
    return this.ensureTamanosLoaded().pipe(switchMap(() => this.load(tipo)));
  }

  reload(): Observable<PerfilDocumentoDef[]> {
    this.loadedForTipo = null;
    this.loadInFlight$ = null;
    this.loadInFlightTipo = null;
    this.urlCache.clear();
    this.tamanosLoaded = false;
    this.tamanosLoad$ = null;
    return this.ensureLoaded();
  }

  /**
   * Invalida catálogo en memoria (p. ej. al crear solicitud nueva).
   * No dispara HTTP; el próximo ensureLoaded() vuelve a cargar según segmento.
   */
  invalidateCatalogCache(): void {
    this.loadedForTipo = null;
    this.loadInFlight$ = null;
    this.loadInFlightTipo = null;
    this.urlCache.clear();
    this.docsSubject.next([]);
    this.progress.setDocumentosConfig([]);
  }

  /**
   * Resuelve URL de imagen ejemplo al abrir modal (no en el listado).
   * Usa neoArchivoPublico/servicios/obtener-url/{id}.
   */
  resolveEjemploUrl(idArchivoPublico: number | null | undefined): Observable<string | null> {
    if (idArchivoPublico == null) {
      return of(null);
    }
    const cached = this.urlCache.get(idArchivoPublico);
    if (cached) {
      return cached;
    }
    const req$ = this.parametros.getUrlArchivoPublico(idArchivoPublico).pipe(
      map((raw: unknown) => {
        const dto = raw as { urlArchivo?: string };
        return dto?.urlArchivo ?? null;
      }),
      catchError(() => of(null)),
      shareReplay(1)
    );
    this.urlCache.set(idArchivoPublico, req$);
    return req$;
  }

  /**
   * Config del modal unificado para un documento del catálogo
   * (tips + una imagen ejemplo + tamaño máx. según PAR).
   */
  modalConfigFor(doc: PerfilDocumentoDef): Observable<PerfilDocumentoModalConfig> {
    return this.ensureTamanosLoaded().pipe(
      switchMap(() => this.resolveEjemploUrl(doc.idArchivoPublico)),
      map((url) =>
        buildDocumentoModalConfig(doc, url, {
          tamanoMaxMb: this.tamanoMaxMbFor(doc),
        })
      )
    );
  }

  /**
   * Igual que `modalConfigFor`, resolviendo el doc por valueKey.
   * Si no hay doc en catálogo, arma un config mínimo con el label dado.
   */
  modalConfigForValueKey(
    valueKey: string,
    fallback?: Partial<PerfilDocumentoDef>
  ): Observable<PerfilDocumentoModalConfig> {
    const doc =
      this.findByValueKey(valueKey) ??
      ({
        id: valueKey,
        codigo: valueKey,
        label: fallback?.label ?? 'Documento',
        seccionId: (fallback?.seccionId ?? 'sobre-ti') as PerfilSeccionId,
        obligatorio: fallback?.obligatorio ?? false,
        valueKey,
        descripcion: fallback?.descripcion,
        guiaUsuario: fallback?.guiaUsuario,
        extensionesValidas: fallback?.extensionesValidas,
        idArchivoPublico: fallback?.idArchivoPublico ?? null,
      } satisfies PerfilDocumentoDef);

    return this.modalConfigFor(doc);
  }

  private ensureTamanosLoaded(): Observable<void> {
    if (this.tamanosLoaded) {
      return of(undefined);
    }
    if (!this.tamanosLoad$) {
      this.tamanosLoad$ = forkJoin({
        docs: this.fetchParamMb(PAR_TAMANO_MAX_ARCHIVOS),
        multimedia: this.fetchParamMb(PAR_TAMANO_MAX_MULTIMEDIA),
      }).pipe(
        tap(({ docs, multimedia }) => {
          this.tamanoDocsMb = docs;
          this.tamanoMultimediaMb = multimedia;
          this.tamanosLoaded = true;
        }),
        map(() => undefined),
        shareReplay(1)
      );
    }
    return this.tamanosLoad$;
  }

  private fetchParamMb(idParametro: string): Observable<number | null> {
    return this.bancoldex
      .getFilterParametro({
        idParametro,
        nombre: null,
        isParFuncional: false,
        pagina: 1,
      })
      .pipe(
        map((response: { data?: ParametroFilterRow[] }) => {
          const raw = response?.data?.[0]?.valorParametro;
          const n = Number(raw);
          return Number.isFinite(n) && n > 0 ? n : null;
        }),
        catchError(() => of(null))
      );
  }

  private load(
    tipoPersona: PerfilTipoDocumentosApi
  ): Observable<PerfilDocumentoDef[]> {
    if (this.loadInFlight$ && this.loadInFlightTipo === tipoPersona) {
      return this.loadInFlight$;
    }

    this.loadInFlightTipo = tipoPersona;
    const juridica = isPerfilJuridica(tipoPersona);
    this.loadInFlight$ = this.parametros.getTiposDocumentosPerfil(tipoPersona).pipe(
      map((raw: unknown) =>
        mapGruposApiToPerfilDocumentos(parseGruposPerfilResponse(raw), {
          juridica,
        })
      ),
      tap((docs) => {
        this.loadedForTipo = tipoPersona;
        this.docsSubject.next(docs);
        this.progress.setDocumentosConfig(docs);
      }),
      catchError(() => {
        this.loadedForTipo = tipoPersona;
        const current = [
          ...(this.progress.getDocumentosConfig() as PerfilDocumentoDef[]),
        ];
        this.docsSubject.next(current);
        return of(current);
      }),
      finalize(() => {
        if (this.loadInFlightTipo === tipoPersona) {
          this.loadInFlight$ = null;
          this.loadInFlightTipo = null;
        }
      }),
      shareReplay(1)
    );
    return this.loadInFlight$;
  }
}
